import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";

import Address from "@mapmycustomers/shared/types/Address";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { CardCollapse, CollapsePanel } from "@mapmycustomers/ui";

import AddressWarning from "@app/component/EntityIncompleteAddressWarning/components/AddressWarning";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import styles from "./IncompleteAddressAlert.module.scss";

interface Props {
  className?: string;
  entity: EntityWithLocation;
  isReadOnlyMember: boolean;
  loading?: boolean;
  onCancel?: () => void;
  onOk?: (address: Address) => void;
  showCancelConfirmation?: boolean;
}

const IncompleteAddressAlert: React.FC<Props> = ({
  className,
  entity,
  isReadOnlyMember,
  loading,
  onCancel,
  onOk,
  showCancelConfirmation,
}) => {
  const intl = useIntl();
  const [open, expand, collapse] = useBoolean(true);

  const handleChange = useCallback(
    (key) => {
      if (key.length === 0) {
        collapse();
      } else {
        expand();
      }
    },
    [collapse, expand]
  );

  const handleOk = useCallback(
    (address: Address) => {
      collapse();
      onOk?.(address);
    },
    [collapse, onOk]
  );

  const handleCancel = useCallback(() => {
    collapse();
    onCancel?.();
  }, [collapse, onCancel]);

  if (!entity.geoAddress?.partial_match) {
    return null;
  }

  return (
    <CardCollapse
      activeKey={open ? "alert" : undefined}
      className={cn(styles.container, className)}
      onChange={handleChange}
      size="small"
    >
      <CollapsePanel
        header={
          <div className={styles.title}>
            <FontAwesomeIcon className={styles.icon} icon={faExclamationTriangle} />
            {intl.formatMessage({
              id: "entity.incompleteAddressAlert.title",
              defaultMessage: "Partially Invalid Address",
              description: "Incomplete address alert - title",
            })}
          </div>
        }
        key="alert"
      >
        <AddressWarning
          addressEditable={!isReadOnlyMember}
          entity={entity}
          loading={loading}
          onCancel={handleCancel}
          onOk={handleOk}
          proposedClassName={styles.proposed}
          showCancelConfirmation={showCancelConfirmation}
        />
      </CollapsePanel>
    </CardCollapse>
  );
};

const mapStateToProps = (state: RootState) => ({
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

export default connect(mapStateToProps)(IncompleteAddressAlert);
